import React, { useEffect, useState } from "react";
import api from "../api";
import { PageHeader, inr } from "../components/UI";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";

export default function Reports() {
  const [monthly, setMonthly] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get("/reports/monthly-sales?months=12").then((r) => setMonthly(r.data));
    api.get("/reports/top-customers?limit=10").then((r) => setTop(r.data));
  }, []);

  const totalRevenue = monthly.reduce((s, m) => s + m.revenue, 0);
  const totalPaid = monthly.reduce((s, m) => s + m.paid, 0);

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Monthly sales trends and top customers" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-flat p-5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">12-month Revenue</div>
          <div className="font-display font-bold text-2xl mt-2 font-mono-num">{inr(totalRevenue)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">12-month Collections</div>
          <div className="font-display font-bold text-2xl mt-2 font-mono-num text-green-700">{inr(totalPaid)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Outstanding</div>
          <div className="font-display font-bold text-2xl mt-2 font-mono-num text-[#C1121F]">{inr(totalRevenue - totalPaid)}</div>
        </div>
      </div>

      <div className="card-flat p-5 mb-6" data-testid="monthly-sales-chart">
        <h3 className="font-display font-bold text-xl mb-4">Monthly Sales & Collections</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthly} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} formatter={(v) => inr(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="revenue" stroke="#C1121F" strokeWidth={2.5} dot={{ r: 4 }} name="Revenue" />
            <Line type="monotone" dataKey="paid" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} name="Paid" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat overflow-hidden" data-testid="top-customers-table">
        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-display font-bold text-xl">Top Customers</h3>
        </div>
        {top.length === 0 ? (
          <div className="p-8 text-sm text-[#94A3B8] text-center">No customer data yet.</div>
        ) : (
          <table className="crm-table">
            <thead>
              <tr>
                <th>#</th><th>Customer</th><th>Type</th>
                <th className="text-right">Orders</th><th className="text-right">Revenue</th>
                <th className="text-right">Paid</th><th className="text-right">Due</th>
              </tr>
            </thead>
            <tbody>
              {top.map((c, idx) => (
                <tr key={c.customer_id}>
                  <td className="font-mono-num text-[#94A3B8]">{idx + 1}</td>
                  <td className="font-semibold">{c.customer_name}</td>
                  <td className="text-[#475569] capitalize">{c.type.replace("_", " ")}</td>
                  <td className="text-right font-mono-num">{c.orders}</td>
                  <td className="text-right font-mono-num font-semibold">{inr(c.revenue)}</td>
                  <td className="text-right font-mono-num text-green-700">{inr(c.paid)}</td>
                  <td className="text-right font-mono-num text-[#C1121F]">{inr(c.due)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
